import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { ArrowLeft, Plus, Trash2, User as UserIcon, Mail, Phone, Calendar, Key, Edit } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminUsers = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newUserDialog, setNewUserDialog] = useState(false);
  const [profileDialog, setProfileDialog] = useState(false);
  
  // New user form
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPhone, setNewPhone] = useState('');
  
  // Profile form
  const [profileEmail, setProfileEmail] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  
  // Password change
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPasswordChange, setNewPasswordChange] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/admin');
    } else {
      loadUsers();
      setProfileEmail(user.email);
      setProfilePhone(user.phone || '');
    }
  }, [user, navigate]);

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to load users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    
    if (!newEmail || !newPassword) {
      toast.error('Completá email y contraseña');
      return;
    }

    try {
      await axios.post(`${API}/admin/users`, {
        email: newEmail,
        password: newPassword,
        phone: newPhone || null,
      });
      
      toast.success('Usuario creado correctamente');
      setNewUserDialog(false);
      setNewEmail('');
      setNewPassword('');
      setNewPhone('');
      loadUsers();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Error al crear usuario');
    }
  };

  const handleDeleteUser = async (email) => {
    if (!confirm(`¿Estás seguro de eliminar al usuario ${email}?`)) return;

    try {
      await axios.delete(`${API}/admin/users/${email}`);
      toast.success('Usuario eliminado');
      loadUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    
    try {
      await axios.put(`${API}/admin/profile`, {
        email: profileEmail,
        phone: profilePhone || null,
      });
      
      toast.success('Perfil actualizado');
      window.location.reload(); // Reload to update auth context
    } catch (error) {
      console.error('Failed to update profile:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar perfil');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (newPasswordChange !== confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    if (newPasswordChange.length < 6) {
      toast.error('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    try {
      await axios.post(`${API}/admin/change-password`, {
        current_password: currentPassword,
        new_password: newPasswordChange,
      });
      
      toast.success('Contraseña actualizada');
      setCurrentPassword('');
      setNewPasswordChange('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Failed to change password:', error);
      toast.error(error.response?.data?.detail || 'Error al cambiar contraseña');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-slate-600">Cargando...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Header */}
      <header className="border-b border-slate-200 bg-white">
        <div className="container-safe flex h-16 items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/dashboard"
              className="flex h-9 w-9 items-center justify-center rounded-lg hover:bg-slate-100"
            >
              <ArrowLeft size={20} />
            </Link>
            <h1 className="text-lg font-semibold text-slate-900">Gestión de Usuarios</h1>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setProfileDialog(true)} data-testid="edit-profile-btn">
              <Edit size={16} className="mr-2" />
              Mi Perfil
            </Button>
            <Button onClick={() => setNewUserDialog(true)} data-testid="new-user-btn">
              <Plus size={16} className="mr-2" />
              Nuevo Usuario
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container-safe py-8">
        <div className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <div className="border-b border-slate-200 p-4">
            <h2 className="text-lg font-semibold text-slate-900">Usuarios Administradores</h2>
            <p className="text-sm text-slate-600">Usuarios con acceso al panel de administración</p>
          </div>

          <div className="divide-y divide-slate-200">
            {users.map((u) => (
              <div key={u.email} className="flex items-center justify-between p-4" data-testid={`user-row-${u.email}`}>
                <div className="flex items-center gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                    <UserIcon size={20} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h3 className="font-semibold text-slate-900">{u.email}</h3>
                      {u.email === user?.email && (
                        <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
                          Tú
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-sm text-slate-600">
                      {u.phone && (
                        <div className="flex items-center gap-1">
                          <Phone size={14} />
                          {u.phone}
                        </div>
                      )}
                      {u.created_at && (
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(u.created_at).toLocaleDateString('es-AR')}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {u.email !== user?.email && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteUser(u.email)}
                    data-testid={`delete-user-${u.email}`}
                  >
                    <Trash2 size={16} />
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* New User Dialog */}
      <Dialog open={newUserDialog} onOpenChange={setNewUserDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Crear Nuevo Usuario</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateUser} className="space-y-4">
            <div>
              <Label htmlFor="newEmail">Email *</Label>
              <Input
                id="newEmail"
                type="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="usuario@ejemplo.com"
                required
              />
            </div>

            <div>
              <Label htmlFor="newPassword">Contraseña *</Label>
              <Input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
              />
            </div>

            <div>
              <Label htmlFor="newPhone">Teléfono (opcional)</Label>
              <Input
                id="newPhone"
                type="tel"
                value={newPhone}
                onChange={(e) => setNewPhone(e.target.value)}
                placeholder="+54 9 11 1234-5678"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setNewUserDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" data-testid="create-user-btn">
                Crear Usuario
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Profile Dialog */}
      <Dialog open={profileDialog} onOpenChange={setProfileDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Mi Perfil</DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="info">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="info">Información</TabsTrigger>
              <TabsTrigger value="password">Contraseña</TabsTrigger>
            </TabsList>

            <TabsContent value="info">
              <form onSubmit={handleUpdateProfile} className="space-y-4">
                <div>
                  <Label htmlFor="profileEmail">Email</Label>
                  <Input
                    id="profileEmail"
                    type="email"
                    value={profileEmail}
                    onChange={(e) => setProfileEmail(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="profilePhone">Teléfono</Label>
                  <Input
                    id="profilePhone"
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => setProfilePhone(e.target.value)}
                    placeholder="+54 9 11 1234-5678"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setProfileDialog(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="update-profile-btn">
                    Guardar Cambios
                  </Button>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="password">
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <Label htmlFor="currentPassword">Contraseña Actual</Label>
                  <Input
                    id="currentPassword"
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="newPasswordChange">Nueva Contraseña</Label>
                  <Input
                    id="newPasswordChange"
                    type="password"
                    value={newPasswordChange}
                    onChange={(e) => setNewPasswordChange(e.target.value)}
                    placeholder="Mínimo 6 caracteres"
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmar Nueva Contraseña</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setProfileDialog(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="change-password-btn">
                    Cambiar Contraseña
                  </Button>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </div>
  );
};
